import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Upload, Menu, X, LogOut, User as UserIcon, Disc3 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { mediaUrl } from "../lib/api";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const navLink = (to, label) => (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase()}`}
      className={`text-sm font-medium tracking-wide transition-colors hover:text-white ${
        location.pathname === to ? "text-white" : "text-zinc-400"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
              <Disc3 className="h-6 w-6 text-[#FFD60A] transition-transform group-hover:rotate-90 duration-500" strokeWidth={1.5} />
              <span className="font-brand text-xl text-[#FFD60A]">NEXTtt<sup className="text-[0.5em] align-super ml-0.5">™</sup></span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              {navLink("/", "Home")}
              {navLink("/discover", "Discover")}
              {navLink("/artists", "Artists")}
            </nav>
          </div>

          <form onSubmit={submitSearch} className="hidden sm:flex flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" strokeWidth={1.5} />
              <input
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search videos, artists, genres"
                className="w-full bg-zinc-900/60 border border-white/10 text-white placeholder-zinc-500 pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A] focus:border-[#FFD60A] transition"
              />
            </div>
          </form>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm font-medium text-zinc-300 hover:text-[#FFD60A] transition-colors">
                    Dashboard
                  </Link>
                )}
                <Link
                  to="/upload"
                  data-testid="nav-upload"
                  className="flex items-center gap-2 bg-[#FFD60A] text-black px-4 py-2 text-sm font-semibold hover:bg-[#FFD60A]/90 transition-colors"
                >
                  <Upload className="h-4 w-4" strokeWidth={2} /> Upload
                </Link>
                <Link to={`/artist/${user.id}`} data-testid="nav-profile" className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white">
                  {user.avatar ? (
                    <img src={mediaUrl(user.avatar)} alt="" className="h-8 w-8 object-cover rounded-full border border-white/20" />
                  ) : (
                    <span className="h-8 w-8 rounded-full bg-zinc-800 flex items-center justify-center"><UserIcon className="h-4 w-4" /></span>
                  )}
                </Link>
                <button data-testid="logout-button" onClick={logout} className="text-zinc-400 hover:text-white transition-colors" title="Log out">
                  <LogOut className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-zinc-300 hover:text-white px-3 py-2">Log in</Link>
                <Link to="/register" data-testid="nav-signup" className="bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-zinc-200 transition-colors">Join NEXTtt</Link>
              </>
            )}
          </div>

          <button className="md:hidden text-white" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-white/10 bg-black px-4 py-4 space-y-3">
          <form onSubmit={submitSearch} className="flex">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="w-full bg-zinc-900 border border-white/10 px-3 py-2 text-sm" data-testid="mobile-search-input" />
          </form>
          <div className="flex flex-col gap-3">
            <Link to="/" onClick={() => setMenuOpen(false)} className="text-zinc-300">Home</Link>
            <Link to="/discover" onClick={() => setMenuOpen(false)} className="text-zinc-300">Discover</Link>
            <Link to="/artists" onClick={() => setMenuOpen(false)} className="text-zinc-300">Artists</Link>
            {user ? (
              <>
                <Link to="/upload" onClick={() => setMenuOpen(false)} className="text-[#FFD60A] font-semibold">Upload</Link>
                <Link to={`/artist/${user.id}`} onClick={() => setMenuOpen(false)} className="text-zinc-300">My Profile</Link>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="text-left text-zinc-400">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)} className="text-zinc-300">Log in</Link>
                <Link to="/register" onClick={() => setMenuOpen(false)} className="text-[#FFD60A] font-semibold">Join NEXTtt</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
